<div class="ks-container ks-user">
  <a class="ks-btn ks-btn--ghost ks-btn--sm ks-user__back" routerLink="/dashboard/users">
    <i class="bi bi-arrow-left" aria-hidden="true"></i>
    Volver a usuarios
  </a>

  <!-- Orden obligatorio: carga -> error de carga -> no encontrado -> ficha. -->
  @if (!userLoaded()) {
    <section class="ks-panel ks-rise-1">
      <app-skeleton [count]="4" height="24px" label="Cargando usuario" />
    </section>
  } @else if (userError()) {
    <section class="ks-panel ks-rise-1">
      <app-empty-state
        icon="wifi-off"
        title="No se pudo cargar el usuario"
        [message]="userError()!"
      >
        <button type="button" class="ks-btn ks-btn--glass" (click)="loadUser()">
          <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
          Reintentar
        </button>
      </app-empty-state>
    </section>
  } @else if (!user()) {
    <section class="ks-panel ks-rise-1">
      <app-empty-state
        icon="person-x"
        title="Usuario no disponible"
        message="No encontramos este usuario. Puede que haya sido eliminado o que el identificador sea incorrecto."
      >
        <a class="ks-btn ks-btn--glass" routerLink="/dashboard/users">Ver todos los usuarios</a>
      </app-empty-state>
    </section>
  } @else {
    <!-- ------------------------------------------------------------------ -->
    <!-- Cabecera                                                            -->
    <!-- ------------------------------------------------------------------ -->
    <section class="ks-panel ks-user__header ks-rise-1">
      <span class="ks-user__avatar" aria-hidden="true">{{ initials() }}</span>

      <div class="ks-user__identity">
        <h1 class="ks-page-title ks-user__name">{{ user()!.fullName }}</h1>
        <p class="ks-user__email">
          <i class="bi bi-envelope" aria-hidden="true"></i>
          {{ user()!.email }}
        </p>

        <div class="ks-user__chips">
          <span class="ks-chip" [class.ks-chip--brand]="user()!.isSudo">
            <i
              class="bi"
              [class.bi-shield-lock-fill]="user()!.isSudo"
              [class.bi-person]="!user()!.isSudo"
              aria-hidden="true"
            ></i>
            {{ user()!.profile }}
          </span>

          <span class="ks-badge" [class]="user()!.status ? 'ks-badge--success' : 'ks-badge--muted'">
            {{ user()!.status ? 'Activo' : 'Inactivo' }}
          </span>
        </div>
      </div>

      <button type="button" class="ks-btn ks-btn--primary ks-user__edit" (click)="edit()">
        <i class="bi bi-pencil" aria-hidden="true"></i>
        Editar
      </button>
    </section>

    <!-- ------------------------------------------------------------------ -->
    <!-- Datos de la cuenta                                                  -->
    <!-- ------------------------------------------------------------------ -->
    <section class="ks-panel ks-rise-2" aria-labelledby="ks-user-data-heading">
      <div class="ks-card__header">
        <h2 class="ks-card__title" id="ks-user-data-heading">Datos de la cuenta</h2>
      </div>

      <dl class="ks-user__data">
        <div class="ks-user__data-item">
          <dt>Identificador</dt>
          <dd class="ks-user__uid">
            <code>{{ user()!.uid }}</code>
            <button
              type="button"
              class="ks-icon-btn"
              aria-label="Copiar el identificador del usuario"
              (click)="copyUid()"
            >
              <i
                class="bi"
                [class.bi-clipboard]="!copied()"
                [class.bi-check-lg]="copied()"
                aria-hidden="true"
              ></i>
            </button>
          </dd>
        </div>

        <div class="ks-user__data-item">
          <dt>Último acceso</dt>
          <dd>
            @if (user()!.lastLoginAt) {
              {{ user()!.lastLoginAt | dateToMoment }}
              <span class="ks-muted">({{ user()!.lastLoginAt | dateToMoment: 'fromNow' }})</span>
            } @else {
              <span class="ks-muted">Nunca ha iniciado sesión</span>
            }
          </dd>
        </div>

        <div class="ks-user__data-item">
          <dt>Fecha de alta</dt>
          <dd>{{ user()!.createdAt | dateToMoment }}</dd>
        </div>

        <div class="ks-user__data-item">
          <dt>Última actualización</dt>
          <dd>{{ user()!.updatedAt | dateToMoment }}</dd>
        </div>
      </dl>
    </section>

    <!-- ------------------------------------------------------------------ -->
    <!-- Dispositivos                                                        -->
    <!-- ------------------------------------------------------------------ -->
    <section class="ks-panel ks-rise-3" aria-labelledby="ks-user-devices-heading">
      <div class="ks-card__header">
        <h2 class="ks-card__title" id="ks-user-devices-heading">Dispositivos</h2>
        @if (devicesLoaded() && !loadingDevices() && !devicesError() && devices().length > 0) {
          <span class="ks-muted ks-nums">{{ devices().length }} registrados</span>
        }
      </div>

      <!-- Orden obligatorio: carga -> error -> vacio -> tabla. El estado vacio
           solo puede aparecer cuando ya se sabe que no hay dispositivos. -->
      @if (!devicesLoaded() || loadingDevices()) {
        <app-skeleton [count]="3" height="40px" label="Cargando dispositivos" />
      } @else if (devicesError()) {
        <app-empty-state
          icon="wifi-off"
          title="No se pudieron cargar los dispositivos"
          [message]="devicesError()!"
        >
          <button type="button" class="ks-btn ks-btn--glass ks-btn--sm" (click)="loadDevices()">
            <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
            Reintentar
          </button>
        </app-empty-state>
      } @else if (devices().length === 0) {
        <app-empty-state
          icon="phone"
          title="Sin dispositivos"
          message="Este usuario todavía no ha vinculado ningún dispositivo con la app móvil."
        />
      } @else {
        <div class="ks-table-wrap">
          <table class="ks-table ks-table--compact ks-table--sticky-first">
            <caption class="visually-hidden">
              Dispositivos vinculados al usuario.
            </caption>

            <thead>
              <tr>
                <th scope="col">Plataforma</th>
                <th scope="col">Identificador</th>
                <th scope="col">Sesión iniciada</th>
                <th scope="col">Estado</th>
                <th scope="col">Alta</th>
              </tr>
            </thead>

            <tbody>
              @for (device of devices(); track device.deviceId) {
                <tr>
                  <td>
                    <span class="ks-user__platform">
                      <i class="bi {{ platformIcon(device) }}" aria-hidden="true"></i>
                      {{ device.platform }}
                    </span>
                  </td>

                  <td>
                    <code class="ks-user__code">{{ device.deviceId }}</code>
                  </td>

                  <td>
                    <span
                      class="ks-badge"
                      [class]="device.logged ? 'ks-badge--info' : 'ks-badge--muted'"
                    >
                      {{ device.logged ? 'Sí' : 'No' }}
                    </span>
                  </td>

                  <td>
                    <span
                      class="ks-badge"
                      [class]="device.status ? 'ks-badge--success' : 'ks-badge--muted'"
                    >
                      {{ device.status ? 'Activo' : 'Inactivo' }}
                    </span>
                  </td>

                  <td>{{ device.createdAt | dateToMoment: 'DD/MM/YYYY' }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </section>

    <!-- ------------------------------------------------------------------ -->
    <!-- Sesiones                                                            -->
    <!-- ------------------------------------------------------------------ -->
    <section class="ks-panel ks-rise-4" aria-labelledby="ks-user-sessions-heading">
      <div class="ks-card__header">
        <h2 class="ks-card__title" id="ks-user-sessions-heading">Sesiones</h2>

        <!--
          ng-select, no <select>: todos los desplegables de la consola deben
          permitir BUSCAR. El id del input interno lo pone `labelForId`, asi
          que el `<label for>` de arriba sigue apuntando al elemento que
          recibe el foco. "Todas las sesiones" queda como item REAL de la
          lista (no como placeholder) para no cambiar el comportamiento del
          select nativo que reemplaza.
        -->
        <div class="ks-user__sessions-filter">
          <label class="visually-hidden" for="ks-user-sessions-filter">Filtrar sesiones</label>
          <ng-select
            labelForId="ks-user-sessions-filter"
            [items]="activeOptions"
            bindLabel="label"
            bindValue="value"
            [ngModel]="activeFilter()"
            (change)="onActiveFilter($event)"
            placeholder="Todas las sesiones"
            notFoundText="Sin resultados"
            clearAllText="Quitar selección"
            loadingText="Cargando…"
            typeToSearchText="Escribe para buscar"
            [searchable]="true"
            [clearable]="true" />
        </div>
      </div>

      <!-- Orden obligatorio: carga -> error -> vacio -> tabla. El estado vacio
           solo puede aparecer cuando ya se sabe que no hay sesiones. -->
      @if (!sessionsLoaded() || loadingSessions()) {
        <app-skeleton [count]="4" height="40px" label="Cargando sesiones" />
      } @else if (sessionsError()) {
        <app-empty-state
          icon="wifi-off"
          title="No se pudieron cargar las sesiones"
          [message]="sessionsError()!"
        >
          <button type="button" class="ks-btn ks-btn--glass ks-btn--sm" (click)="loadSessions()">
            <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
            Reintentar
          </button>
        </app-empty-state>
      } @else if (sessions().length === 0) {
        @if (activeFilter() !== '') {
          <app-empty-state
            icon="funnel"
            title="Sin sesiones con ese filtro"
            message="No hay sesiones que coincidan. Cambia el filtro para ver el resto del historial."
          />
        } @else {
          <app-empty-state
            icon="laptop"
            title="Sin sesiones"
            message="Este usuario no ha iniciado sesión desde ningún dispositivo."
          />
        }
      } @else {
        <div class="ks-table-wrap">
          <table class="ks-table ks-table--compact ks-table--sticky-first">
            <caption class="visually-hidden">
              Sesiones del usuario.
            </caption>

            <thead>
              <tr>
                <th scope="col">Dispositivo</th>
                <th scope="col">App</th>
                <th scope="col">Ubicación</th>
                <th scope="col">Inicio</th>
                <th scope="col">Estado</th>
                <th scope="col" class="ks-user__th-actions">Acciones</th>
              </tr>
            </thead>

            <tbody>
              @for (session of sessions(); track session.sessionId) {
                <tr>
                  <td>
                    <span class="ks-user__strong">{{ session.deviceName || session.model }}</span>
                    <span class="ks-user__sub">{{ session.manufacturer }} {{ session.model }}</span>
                  </td>

                  <td>
                    <span class="ks-nums">{{ session.appVersion }}</span>
                    <span class="ks-user__sub">Build {{ session.appBuild }}</span>
                  </td>

                  <td>
                    <span>{{ session.location || '—' }}</span>
                    <span class="ks-user__sub ks-nums">{{ session.ip }}</span>
                  </td>

                  <td>{{ session.createdAt | dateToMoment }}</td>

                  <td>
                    <span
                      class="ks-badge"
                      [class]="session.active ? 'ks-badge--success' : 'ks-badge--muted'"
                    >
                      {{ session.active ? 'Activa' : 'Cerrada' }}
                    </span>
                  </td>

                  <td>
                    <div class="ks-table__actions">
                      <button
                        type="button"
                        class="ks-icon-btn ks-icon-btn--danger"
                        [disabled]="!session.active"
                        [attr.aria-label]="
                          'Cerrar la sesión de ' + (session.deviceName || session.model)
                        "
                        (click)="closeSession(session)"
                      >
                        <i class="bi bi-power" aria-hidden="true"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <app-pagination
          [total]="sessionsTotal()"
          [page]="sessionsPage()"
          [pageSize]="sessionsPageSize()"
          (pageChange)="onSessionsPageChange($event)"
        />
      }
    </section>
  }
</div>
